import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/title";
import type { Notification } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";

export function NotificationsPage() {
  usePageTitle("Notifications");
  const queryClient = useQueryClient();
  const { data: notifications, isLoading } = useQuery({
    queryKey: ["notifications", "me"],
    queryFn: () => api.get<Notification[]>("/api/v1.0/notifications/me"),
  });

  const { mutate: markRead } = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1.0/notifications/${id}/read`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications", "me"] }),
  });

  if (isLoading) return <FullPageSpinner />;

  return (
    <div className="flex flex-col gap-6">

      {!notifications?.length ? (
        <EmptyState icon={Bell} title="You're all caught up" />
      ) : (
        <Card>
          <CardBody className="flex flex-col divide-y divide-slate-100">
            {notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => !n.read_at && markRead(n.id)}
                className={clsx("flex flex-col items-start gap-1 px-1 py-3 text-left", !n.read_at && "bg-brand-50/50")}
              >
                <div className="flex w-full items-center justify-between">
                  <span className="font-medium text-slate-800">{n.title}</span>
                  <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
                </div>
                {n.body && <p className="text-sm text-slate-500">{n.body}</p>}
              </button>
            ))}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
